import { useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../../stores/auth';
import { SkeletonAdminOverview } from '../../components/Skeleton';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface AdminStats {
  totalTenants: number;
  totalUsers: number;
  activeSubscriptions: number;
  trialSubscriptions: number;
}

interface AdminStatsResponse {
  success: boolean;
  data?: AdminStats;
  error?: { message?: string };
}

export function AdminOverviewPage(): ReactNode {
  const { accessToken } = useAuthStore();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats(): Promise<void> {
      try {
        const res = await fetch(`${API_URL}/admin/stats`, {
          headers: {
            Authorization: `Bearer ${accessToken ?? ''}`,
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch admin stats');
        }

        const data = (await res.json()) as AdminStatsResponse;
        if (data.success && data.data !== undefined) {
          setStats(data.data);
        } else {
          throw new Error(data.error?.message ?? 'Failed to fetch stats');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }

    void fetchStats();
  }, [accessToken]);

  if (loading) {
    return <SkeletonAdminOverview />;
  }

  if (error !== null) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Admin Overview</h1>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Tenants"
          value={stats?.totalTenants ?? 0}
          color="blue"
        />
        <StatCard
          title="Total Users"
          value={stats?.totalUsers ?? 0}
          color="green"
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.activeSubscriptions ?? 0}
          color="purple"
        />
        <StatCard
          title="Trial Subscriptions"
          value={stats?.trialSubscriptions ?? 0}
          color="orange"
        />
      </div>

      <div className="mt-8 rounded-lg bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex gap-4">
          <a
            href="/admin/tenants"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Manage Tenants
          </a>
          <a
            href="/admin/users"
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            Manage Users
          </a>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  color,
}: {
  title: string;
  value: number;
  color: 'blue' | 'green' | 'purple' | 'orange';
}): ReactNode {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
    orange: 'bg-orange-50 text-orange-700',
  };

  return (
    <div className={`rounded-lg p-6 ${colorClasses[color]}`}>
      <p className="text-sm font-medium opacity-75">{title}</p>
      <p className="mt-2 text-3xl font-bold">{value.toLocaleString()}</p>
    </div>
  );
}
